import type { AmmTelemetryView, AmmVenueState, Instrument, PairListing } from '@nutrimatic/sdk'
import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'

import type { PoolSummary } from '@/data/marketApi/poolsTypes'
import { poolAddressFromVenueId } from '@/data/nutrimatic/ids'
import {
  formatNutrimaticChange,
  formatNutrimaticPrice,
  formatNutrimaticUsd,
} from '@/features/market/nutrimaticFormat'

import type { NutrimaticPoolRow } from './useNutrimaticMarketHub'

function instrumentAddress(instrument: Instrument | undefined): string {
  return instrument?.onChainAddress?.toLowerCase() ?? NATIVE_TOKEN_ADDRESS
}

function quantityDisplay(raw: { raw: bigint | string; decimals: number } | undefined): string {
  if (!raw) return '—'
  try {
    const value = typeof raw.raw === 'bigint' ? raw.raw : BigInt(raw.raw)
    const denom = 10n ** BigInt(raw.decimals)
    const whole = value / denom
    const frac = value % denom
    if (frac === 0n) return whole.toString()
    const fracStr = frac.toString().padStart(raw.decimals, '0').replace(/0+$/, '')
    return fracStr ? `${whole}.${fracStr}` : whole.toString()
  } catch {
    return '—'
  }
}

/** Adapt Nutrimatic pool row → existing LiquidityPoolProfile / PoolSummary UI shape. */
export function nutrimaticPoolRowToSummary(row: NutrimaticPoolRow): PoolSummary {
  const pairAddress =
    row.poolAddress ?? poolAddressFromVenueId(row.listing.venueId) ?? row.listing.venueId
  const base = row.base
  const quote = row.quote
  const view = row.view
  const baseSym = base?.symbol ?? row.listing.baseInstrumentId
  const quoteSym = quote?.symbol ?? row.listing.quoteInstrumentId
  const token0Addr = instrumentAddress(base)
  const token1Addr = instrumentAddress(quote)

  return {
    id: pairAddress,
    pairAddress,
    pair: `${baseSym}/${quoteSym}`,
    token0: {
      address: token0Addr,
      symbol: baseSym,
      catalogAddress: token0Addr,
    },
    token1: {
      address: token1Addr,
      symbol: quoteSym,
      catalogAddress: token1Addr,
    },
    baseToken: {
      address: token0Addr,
      symbol: baseSym,
      catalogAddress: token0Addr,
    },
    quoteToken: {
      address: token1Addr,
      symbol: quoteSym,
      catalogAddress: token1Addr,
    },
    fee: '0.30%',
    tvl: formatNutrimaticUsd(view?.tvlUsd),
    tvlUsd: view?.tvlUsd ?? 0,
    volume24h: formatNutrimaticUsd(view?.volume24hUsd),
    volume7d: undefined,
    apy: '—',
    price: formatNutrimaticPrice(view?.spot),
    change24h: formatNutrimaticChange(view?.change24h),
    trades24h: undefined,
    reserves0: '—',
    reserves1: '—',
    token0SharePct: undefined,
    token1SharePct: undefined,
  }
}

export function overlayLivePoolMetrics(
  summary: PoolSummary,
  pool: AmmTelemetryView | null | undefined,
  state?: AmmVenueState | null,
): PoolSummary {
  if (!pool && !state) return summary
  const r0 = state?.reserves?.[0]
  const r1 = state?.reserves?.[1]
  const baseSym = summary.baseToken?.symbol ?? summary.token0.symbol
  const quoteSym = summary.quoteToken?.symbol ?? summary.token1.symbol
  return {
    ...summary,
    tvl: pool ? formatNutrimaticUsd(pool.tvlUsd) : summary.tvl,
    tvlUsd: pool?.tvlUsd ?? summary.tvlUsd,
    volume24h: pool ? formatNutrimaticUsd(pool.volume24hUsd) : summary.volume24h,
    price: pool ? formatNutrimaticPrice(pool.spot ?? state?.spot) : formatNutrimaticPrice(state?.spot) || summary.price,
    change24h: pool ? formatNutrimaticChange(pool.change24h) : summary.change24h,
    reserves0: r0 ? `${quantityDisplay(r0)} ${baseSym}` : summary.reserves0,
    reserves1: r1 ? `${quantityDisplay(r1)} ${quoteSym}` : summary.reserves1,
  }
}

export function findPoolRowByParam(
  rows: readonly NutrimaticPoolRow[],
  pairs: readonly PairListing[],
  param: string | undefined,
): NutrimaticPoolRow | undefined {
  if (!param) return undefined
  const key = decodeURIComponent(param).toLowerCase()
  const byAddress = rows.find((r) => r.poolAddress === key)
  if (byAddress) return byAddress
  const byLabel = rows.find((r) => r.listing.pairLabel.toLowerCase() === key)
  if (byLabel) return byLabel
  const listing = pairs.find(
    (p) =>
      p.pairLabel.toLowerCase() === key ||
      p.venueId.toLowerCase().endsWith(`:${key}`) ||
      p.venueId.toLowerCase() === key,
  )
  if (!listing) return undefined
  return (
    rows.find((r) => r.listing.venueId === listing.venueId) ?? {
      listing,
      poolAddress: poolAddressFromVenueId(listing.venueId),
      view: null,
      base: undefined,
      quote: undefined,
    }
  )
}
